import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect, useDispatch } from 'react-redux';
import * as actions from './redux/actions';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFolderOpen,
  faPlusCircle,
  faTv,
  faLock,
  faDatabase,
  faUsersCog,
  faScroll,
  faFileAlt,
} from '@fortawesome/free-solid-svg-icons';
import T from 'i18n';
import CaseCreateModal from 'features/case/CaseCreateModal';
import {
  selectIsAdmin,
  selectLastFolder,
  selectCases,
  selectFetchCasesFailureStatus,
  selectIsOrganizationAdmin,
  selectFilterCasesByTemplate,
  selectCaseTemplates,
} from 'common/selectors';
import classnames from 'classnames';
import Button from 'react-bootstrap/Button';
import { updateUserBehaviour } from 'features/common/redux/updateUserBehaviour';
import FilterCases from './FilterCases';
import FilterCaseTemplates from './FilterCaseTemplates';
import history from 'common/history';
import { AddCircle, Folder, FilterList } from '@mui/icons-material';
import {
  Fab,
  Tooltip,
  IconButton,
  Menu,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { HOME_TEMPLATE_FILTER_NONE } from './redux/constants';

const caseFilterLimit = 10;

const formatNumber = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString() : '—';

const formatStorageSize = (bytes) => {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) {
    return '—';
  }

  if (bytes === 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  const formatted = value >= 10 ? value.toFixed(0) : value.toFixed(1);

  return `${formatted} ${units[exponent]}`;
};

const formatGeneratedDate = (value) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const CardLink = ({ link, icon, icon2, labelKey, lastFolder, trackCardButtonClick }) => {
  return (
    <Link
      to={lastFolder ? `${link}/${lastFolder}` : link}
      onClick={() => trackCardButtonClick(labelKey)}
      target="_blank"
      className="case-card-link"
    >
      <span className="fa-layers fa-fw">
        <FontAwesomeIcon icon={icon} />
        {icon2 && <FontAwesomeIcon icon={icon2} transform="shrink-5 up-5 right-6" />}
      </span>
      {T.translate(labelKey)}
    </Link>
  );
};

const Card = ({
  id,
  name,
  claimNumber,
  canBrowseDocuments,
  canViewDocuments,
  canJoinConference,
  canStream,
  canViewDocumentsPrivate,
  canJoinConferencePrivate,
  canStreamPrivate,
  canViewCaseAdmin,
  canViewModerator,
  canViewRealTime,
  lastFolder,
  showTranscript,
  showTranscriptPrivate,
  useNewRTViewer,
}) => {
  const dispatch = useDispatch();
  const showDocumentsLink = canBrowseDocuments;
  const documentsLink = `/case/${id}`;
  const showHearingRoomLink =
    canJoinConference || canStream || canViewModerator || canViewDocuments;
  const hearingRoomLink =
    (canJoinConference || canStream) && canViewDocuments
      ? `/case/${id}/public-hearing-room`
      : canViewDocuments
        ? `/case/${id}/public-hearing-room/document-presentation`
        : canJoinConference || canStream
          ? `/case/${id}/public-hearing-room/conference`
          : '/';
  const showPrivateRoomLink =
    (canJoinConferencePrivate || canStreamPrivate) && canViewDocumentsPrivate;
  const showTranscriptLink = useNewRTViewer
    ? canViewRealTime
    : showHearingRoomLink && showTranscript && canViewRealTime;
  const showTranscriptPrivateLink = showHearingRoomLink && showTranscriptPrivate && canViewRealTime;
  const privateRoomLink = `/case/${id}/private-team-room`;
  const showHearingRoomDocsOnlyLink = canViewDocuments;
  const hearingRoomDocsOnlyLink = `/case/${id}/public-hearing-room/document-presentation`;
  const showCaseAdminLink = canViewCaseAdmin;
  const caseAdminLink = `/case/${id}/settings/case-admin`;
  const transcriptLink = `/case/${id}/public-hearing-room-transcript`;
  const transcriptLinkPrivate = `/case/${id}/private-hearing-room-transcript`;

  const links = [
    showDocumentsLink && {
      link: documentsLink,
      icon: faDatabase,
      labelKey: 'home.dataRoom',
      lastFolder,
    },
    showHearingRoomLink &&
      hearingRoomLink !== hearingRoomDocsOnlyLink && {
        link: hearingRoomLink,
        icon: faTv,
        labelKey: 'home.hearingRoom',
      },
    showHearingRoomDocsOnlyLink && {
      link: hearingRoomDocsOnlyLink,
      icon: faFileAlt,
      labelKey: 'home.hearingRoomDocsOnly',
    },
    showPrivateRoomLink && {
      link: privateRoomLink,
      icon: faTv,
      icon2: faLock,
      labelKey: 'home.privateRoom',
    },
    showCaseAdminLink && {
      link: caseAdminLink,
      icon: faUsersCog,
      labelKey: 'home.caseAdmin',
    },
    showTranscriptLink && {
      link: transcriptLink,
      icon: faScroll,
      labelKey: 'home.transcript',
    },
    showTranscriptPrivateLink && {
      link: transcriptLinkPrivate,
      icon: faScroll,
      icon2: faLock,
      labelKey: 'home.transcriptPrivate',
    },
  ].filter(Boolean);

  const trackCardButtonClick = (labelKey) => {
    dispatch(
      updateUserBehaviour({
        caseId: id,
        name: `Home Card ${T.translate(labelKey)} Clicked`,
      }),
    );
  };

  const titleStyles = getRegularCardTitleStyles(name);

  return (
    <div className="case-card">
      <div className="links">
        {links.map((props, index) => (
          <CardLink key={index} {...props} trackCardButtonClick={trackCardButtonClick} />
        ))}
      </div>
      {links.length > 0 ? (
        <Link
          to={links[0].link}
          className="title"
          style={titleStyles}
          onClick={() => trackCardButtonClick('home.caseTitle')}
        >
          <span>{name}</span>
          <span>{claimNumber}</span>
        </Link>
      ) : (
        <div
          className="title"
          style={titleStyles}
          onClick={() => trackCardButtonClick('home.caseTitle')}
        >
          {name}
        </div>
      )}
    </div>
  );
};

const buildStatLine = (value, suffix) => {
  const formatted = value === '—' ? null : value;
  if (!formatted) {
    return '—';
  }
  return suffix ? `${formatted} ${suffix}` : formatted;
};

const getNameDisplayStyles = (name) => {
  const length = typeof name === 'string' ? name.length : 0;

  // Handle extremely long names (120+ chars)
  if (length > 120) {
    return {
      '--cc-name-font-size': '0.65rem',
      '--cc-name-line-height': '1.0em',
      '--cc-name-lines': 4,
    };
  }

  // Handle very long names (100+ chars)
  if (length > 100) {
    return {
      '--cc-name-font-size': '0.7rem',
      '--cc-name-line-height': '1.02em',
      '--cc-name-lines': 4,
    };
  }

  // Handle long names (80+ chars)
  if (length > 80) {
    return {
      '--cc-name-font-size': '0.75rem',
      '--cc-name-line-height': '1.05em',
      '--cc-name-lines': 3,
    };
  }

  // Handle moderately long names (60+ chars)
  if (length > 60) {
    return {
      '--cc-name-font-size': '0.8rem',
      '--cc-name-line-height': '1.08em',
      '--cc-name-lines': 3,
    };
  }

  // Handle medium names (45+ chars)
  if (length > 45) {
    return {
      '--cc-name-font-size': '0.9rem',
      '--cc-name-line-height': '1.12em',
      '--cc-name-lines': 3,
    };
  }

  // Handle slightly long names (35+ chars)
  if (length > 35) {
    return {
      '--cc-name-font-size': '0.98rem',
      '--cc-name-line-height': '1.18em',
      '--cc-name-lines': 2,
    };
  }

  // Handle names over 25 chars
  if (length > 25) {
    return {
      '--cc-name-font-size': '1.05rem',
      '--cc-name-line-height': '1.2em',
      '--cc-name-lines': 2,
    };
  }

  // Default for short names
  return {};
};

const getRegularCardTitleStyles = (name) => {
  const length = typeof name === 'string' ? name.length : 0;

  // Handle extremely long names (150+ chars) - more space available in regular cards
  if (length > 150) {
    return {
      '--title-font-size': '0.9rem',
      '--title-line-height': '1.1em',
    };
  }

  // Handle very long names (120+ chars)
  if (length > 120) {
    return {
      '--title-font-size': '1.0rem',
      '--title-line-height': '1.2em',
    };
  }

  // Handle long names (90+ chars)
  if (length > 90) {
    return {
      '--title-font-size': '1.1rem',
      '--title-line-height': '1.3em',
    };
  }

  // Handle moderately long names (60+ chars)
  if (length > 60) {
    return {
      '--title-font-size': '1.2rem',
      '--title-line-height': '1.4em',
    };
  }

  // Handle medium names (40+ chars)
  if (length > 40) {
    return {
      '--title-font-size': '1.3rem',
      '--title-line-height': '1.5em',
    };
  }

  // Default for short names (use original size)
  return {};
};

const CardAdmin = ({ id, name, stats }) => {
  const hasStats = stats && typeof stats === 'object';
  const statsLines = hasStats
    ? [
        buildStatLine(formatNumber(stats.documentCount), 'files'),
        buildStatLine(formatNumber(stats.pageCount), 'pages'),
        buildStatLine(formatStorageSize(stats.storageSizeinBytes)),
        buildStatLine(formatGeneratedDate(stats.generated)),
      ]
    : [];

  const nameStyles = getNameDisplayStyles(name);
  const statsContent = hasStats ? (
    <div className="case-card-admin__stats">
      {statsLines.map((line, index) => (
        <div key={index} className="case-card-admin__stats-line">
          {line}
        </div>
      ))}
    </div>
  ) : (
    <div className="case-card-admin__stats-empty">No stats available</div>
  );

  // Check if case name contains "delete me" to show grey icon
  const isDeleteMeCase = name && name.includes('delete me');
  const iconColor = isDeleteMeCase ? 'disabled' : 'primary';

  return (
    <div key={id} className="case-card-admin">
      <Link to={`/case/${id}`} className="case-card-admin__link">
        <div className="case-card-admin__inner">
          <div className="case-card-admin__icon-wrapper">
            <Folder className="case-card-admin__icon" color={iconColor} />
          </div>
          <div className="case-card-admin__content">
            <span className="case-card-admin__name" style={nameStyles}>
              {name}
            </span>
            {statsContent}
          </div>
        </div>
      </Link>
    </div>
  );
};

const AddButton = ({ onClick }) => (
  <button
    key="add-new-case"
    className="case-card-admin new-case-card"
    onClick={onClick}
    type="button"
  >
    <div className="case-card-admin__link">
      <div className="case-card-admin__inner">
        <div className="case-card-admin__icon-wrapper">
          <AddCircle className="case-card-admin__icon" color="primary" />
        </div>
        <div className="case-card-admin__content new-case-content">
          <span className="case-card-admin__name new-case-text">
            {T.translate('home.createNewCase')}
          </span>
        </div>
      </div>
    </div>
  </button>
);

export class Cases extends Component {
  static propTypes = {
    cases: PropTypes.array.isRequired,
    actions: PropTypes.object.isRequired,
    filterCasesByTemplate: PropTypes.bool,
    caseTemplates: PropTypes.array,
  };

  state = {
    showModal: false,
    loc: '',
    cases: this.props.cases,
    searchValue: '',
    selectedTemplates: [],
    hideDeleteMeCases: true, // Hide "delete me" cases by default
    showStatsOnlyCases: false, // Show all cases by default
    filterMenuAnchor: null,
  };

  componentDidMount() {
    this.applyFilters();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.caseTemplates !== this.props.caseTemplates) {
      const availableTemplates = (this.props.caseTemplates || []).map(({ value }) => value);
      const filteredSelectedTemplates = this.state.selectedTemplates.filter((value) =>
        availableTemplates.includes(value),
      );
      if (filteredSelectedTemplates.length !== this.state.selectedTemplates.length) {
        this.setState({ selectedTemplates: filteredSelectedTemplates });
        return;
      }
    }

    if (
      prevProps.cases !== this.props.cases ||
      prevState.searchValue !== this.state.searchValue ||
      prevState.selectedTemplates !== this.state.selectedTemplates ||
      prevState.hideDeleteMeCases !== this.state.hideDeleteMeCases ||
      prevState.showStatsOnlyCases !== this.state.showStatsOnlyCases
    ) {
      this.applyFilters();
    }
  }

  handleSearchChange = (value) => {
    this.setState({ searchValue: value || '' });
  };

  handleTemplateAdd = (templateValue) => {
    this.setState((prevState) => {
      if (prevState.selectedTemplates.includes(templateValue)) {
        return null;
      }
      return { selectedTemplates: [...prevState.selectedTemplates, templateValue] };
    });
  };

  handleTemplateRemove = (templateValue) => {
    this.setState((prevState) => ({
      selectedTemplates: prevState.selectedTemplates.filter((value) => value !== templateValue),
    }));
  };

  handleFilterMenuOpen = (event) => {
    this.setState({ filterMenuAnchor: event.currentTarget });
  };

  handleFilterMenuClose = () => {
    this.setState({ filterMenuAnchor: null });
  };

  handleDeleteMeToggle = () => {
    this.setState((prevState) => ({
      hideDeleteMeCases: !prevState.hideDeleteMeCases,
    }));
  };

  handleStatsOnlyToggle = () => {
    this.setState((prevState) => ({
      showStatsOnlyCases: !prevState.showStatsOnlyCases,
    }));
  };

  applyFilters = () => {
    const { searchValue, selectedTemplates, hideDeleteMeCases, showStatsOnlyCases } = this.state;
    const { cases } = this.props;
    const normalizedSearch = (searchValue || '').trim().toLowerCase();

    let filteredCases = Array.isArray(cases) ? cases : [];

    // Apply search filter
    if (normalizedSearch.length > 0) {
      filteredCases = filteredCases.filter(({ name, claimNumber }) => {
        const caseName = (name || '').toLowerCase();
        const caseClaimNumber = claimNumber ? String(claimNumber).toLowerCase() : '';
        return caseName.includes(normalizedSearch) || caseClaimNumber.includes(normalizedSearch);
      });
    }

    // Apply template filter
    if (selectedTemplates.length > 0) {
      filteredCases = filteredCases.filter(({ fromTemplate }) => {
        const templateValue =
          typeof fromTemplate === 'string' && fromTemplate.length > 0
            ? fromTemplate
            : HOME_TEMPLATE_FILTER_NONE;
        return selectedTemplates.includes(templateValue);
      });
    }

    // Apply "delete me" filter
    if (hideDeleteMeCases) {
      filteredCases = filteredCases.filter(({ name }) => {
        const caseName = (name || '').toLowerCase();
        return !caseName.includes('delete me');
      });
    }

    // Apply stats-only filter
    if (showStatsOnlyCases) {
      filteredCases = filteredCases.filter(({ stats }) => {
        return stats != null;
      });
    }

    this.setState({ cases: filteredCases });
  };

  render() {
    const showTemplateFilter =
      this.props.filterCasesByTemplate &&
      this.props.cases.length > 0 &&
      (this.props.caseTemplates || []).length > 0;
    const showSearchControl = this.props.cases.length > caseFilterLimit;
    const showCreateButton = this.props.isOrganizationAdmin && this.props.cases.length > 0;
    const showCaseFilter = this.props.isSystemAdmin && this.props.cases.length > 0;
    const showToolbar =
      showTemplateFilter || showSearchControl || showCreateButton || showCaseFilter;

    return (
      <>
        {showToolbar && (
          <div className="case-filter-toolbar">
            <div className="case-filter-toolbar__center">
              {showSearchControl && (
                <FilterCases
                  value={this.state.searchValue}
                  onSearchChange={this.handleSearchChange}
                />
              )}
              {showTemplateFilter && (
                <FilterCaseTemplates
                  templates={this.props.caseTemplates}
                  selectedTemplates={this.state.selectedTemplates}
                  onAdd={this.handleTemplateAdd}
                  onRemove={this.handleTemplateRemove}
                />
              )}
              {showCaseFilter && (
                <div style={{ marginLeft: showTemplateFilter ? '8px' : '0' }}>
                  <Tooltip title="Filter cases" placement="top" arrow>
                    <IconButton
                      size="small"
                      onClick={this.handleFilterMenuOpen}
                      color={
                        !this.state.hideDeleteMeCases || this.state.showStatsOnlyCases
                          ? 'primary'
                          : 'default'
                      }
                    >
                      <FilterList />
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={this.state.filterMenuAnchor}
                    open={Boolean(this.state.filterMenuAnchor)}
                    onClose={this.handleFilterMenuClose}
                  >
                    <MenuItem onClick={(e) => e.stopPropagation()}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={this.state.hideDeleteMeCases}
                            onChange={this.handleDeleteMeToggle}
                          />
                        }
                        label={
                          this.state.hideDeleteMeCases ? "Show delete me's" : "Hide delete me's"
                        }
                        onChange={this.handleDeleteMeToggle}
                      />
                    </MenuItem>
                    <MenuItem onClick={(e) => e.stopPropagation()}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={this.state.showStatsOnlyCases}
                            onChange={this.handleStatsOnlyToggle}
                          />
                        }
                        label={
                          this.state.showStatsOnlyCases
                            ? 'Show stats only cases'
                            : 'Hide stats only cases'
                        }
                        onChange={this.handleStatsOnlyToggle}
                      />
                    </MenuItem>
                  </Menu>
                </div>
              )}
            </div>
            {showCreateButton && (
              <div className="case-filter-toolbar__actions">
                <Tooltip title={T.translate('home.createNewCase')} placement="top" arrow>
                  <Fab
                    size="small"
                    color="primary"
                    onClick={() => this.setState({ showModal: true })}
                  >
                    <AddCircle />
                  </Fab>
                </Tooltip>
              </div>
            )}
          </div>
        )}
        <div
          className={classnames('home-cases', {
            'justify-content-center': this.props.cases.length <= 3,
          })}
        >
          {(this.props.isSystemAdmin ||
            (this.props.isOrganizationAdmin && this.props.cases.length === 0)) && (
            <AddButton onClick={() => this.setState({ showModal: true })} />
          )}
          {this.state.cases.map((props) =>
            this.props.isSystemAdmin ? (
              <CardAdmin key={props.id} {...props} />
            ) : (
              <Card key={props.id} {...props} lastFolder={this.props.lastFolder[props.id]} />
            ),
          )}
          {this.props.cases.length > 0 && this.state.cases.length === 0 && (
            <div className="justify-message">{T.translate('home.noCaseFoundMessage')}</div>
          )}
          {this.props.cases.length === 0 &&
            (!this.props.fetchCasesFailureStatus ? (
              !this.props.isOrganizationAdmin && <div>{T.translate('home.noCasesMessage')}</div>
            ) : (
              <div>{T.translate('home.fetchCasesProbMessage')}</div>
            ))}
          {
            <CaseCreateModal
              show={this.state.showModal}
              handleClose={() => this.setState({ showModal: false })}
              handleSave={(caseDetails) =>
                this.props.actions
                  .createCase(caseDetails)
                  .then(({ id: currentCaseId }) => {
                    this.props.actions.fetchCases();
                    if (this.state.showModal) history.push(`/case/${currentCaseId}`);
                  })

                  .catch((err) => console.error(err))
              }
            />
          }
        </div>
      </>
    );
  }
}

/* istanbul ignore next */
function mapStateToProps(state) {
  const fetchCasesFailureStatus = selectFetchCasesFailureStatus(state);
  const cases = selectCases(state);
  const isSystemAdmin = selectIsAdmin(state);
  const isOrganizationAdmin = selectIsOrganizationAdmin(state);
  const lastFolder = selectLastFolder(state);
  const filterCasesByTemplate = selectFilterCasesByTemplate(state);
  const caseTemplates = selectCaseTemplates(state);
  return {
    fetchCasesFailureStatus,
    cases,
    isSystemAdmin,
    isOrganizationAdmin,
    lastFolder,
    filterCasesByTemplate,
    caseTemplates,
  };
}

/* istanbul ignore next */
function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...actions }, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Cases);
